import { useId } from 'react';

import {
  SALT_BRIDGE,
  formatSigned,
  type ElectrolysisCase,
  type GalvanicCell,
  type ReductionCouple,
} from './electrochemicalCells';

/** Schematic geometry (SVG user units). Every shape is positioned from model state, not artwork. */
const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 340;
const VESSEL_TOP = 150;
const VESSEL_BOTTOM = 290;
const LIQUID_TOP = 185;
const WIRE_Y = 50;
const ELECTRODE_TOP = 105;
const ELECTRODE_WIDTH = 24;
const ELECTRODE_HEIGHT = 150;
const LEFT_VESSEL = { x: 50, width: 200 };
const RIGHT_VESSEL = { x: 390, width: 200 };
const BRIDGE_Y = 125;
const BRIDGE_LEFT_X = 215;
const BRIDGE_RIGHT_X = 425;

function centreOf(vessel: { x: number; width: number }) {
  return vessel.x + vessel.width / 2;
}

function ArrowMarker({ id, className }: { id: string; className: string }) {
  return (
    <marker id={id} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
      <path d="M0 0 L10 5 L0 10 z" className={className} />
    </marker>
  );
}

function Vessel({
  vessel,
  liquidClass,
  electrode,
  electrodeInert,
  electrolyte,
}: {
  vessel: { x: number; width: number };
  liquidClass: string;
  electrode: string;
  electrodeInert: boolean;
  electrolyte: string;
}) {
  const centre = centreOf(vessel);
  return (
    <g>
      <rect
        x={vessel.x + 2}
        y={LIQUID_TOP}
        width={vessel.width - 4}
        height={VESSEL_BOTTOM - LIQUID_TOP - 2}
        className={liquidClass}
      />
      <path
        d={`M${vessel.x} ${VESSEL_TOP} V${VESSEL_BOTTOM} H${vessel.x + vessel.width} V${VESSEL_TOP}`}
        className="fill-none stroke-ink"
        strokeWidth="3"
      />
      <rect
        x={centre - ELECTRODE_WIDTH / 2}
        y={ELECTRODE_TOP}
        width={ELECTRODE_WIDTH}
        height={ELECTRODE_HEIGHT}
        rx="3"
        className={electrodeInert ? 'fill-surface stroke-slate2' : 'fill-slate2 stroke-ink'}
        strokeWidth="2"
        strokeDasharray={electrodeInert ? '4 3' : undefined}
      />
      <text x={centre + 18} y={ELECTRODE_TOP + 14} className="fill-ink text-[15px] font-extrabold">
        {electrode}
      </text>
      <text x={centre} y={VESSEL_BOTTOM - 10} textAnchor="middle" className="fill-ink text-[13px] font-bold">
        {electrolyte}
      </text>
    </g>
  );
}

export function GalvanicSchematic({
  first,
  second,
  cell,
  revealed,
}: {
  first: ReductionCouple;
  second: ReductionCouple;
  cell: GalvanicCell | null;
  revealed: boolean;
}) {
  const markerId = `${useId().replace(/:/g, '')}-arrow`;
  const leftX = centreOf(LEFT_VESSEL);
  const rightX = centreOf(RIGHT_VESSEL);
  const shown = revealed && cell ? cell : null;
  const anodeIsLeft = shown ? shown.anode.id === first.id : true;
  const roleFor = (couple: ReductionCouple, fallback: string) => {
    if (!shown) return fallback;
    return couple.id === shown.anode.id ? 'Anode (−) · oxidation' : 'Cathode (+) · reduction';
  };
  const meterGap = 34;
  const electronSegments: [number, number][] = anodeIsLeft
    ? [
        [leftX + 20, VIEW_WIDTH / 2 - meterGap],
        [VIEW_WIDTH / 2 + meterGap, rightX - 20],
      ]
    : [
        [rightX - 20, VIEW_WIDTH / 2 + meterGap],
        [VIEW_WIDTH / 2 - meterGap, leftX + 20],
      ];
  const cathodeLegX = anodeIsLeft ? BRIDGE_RIGHT_X : BRIDGE_LEFT_X;
  const anodeLegX = anodeIsLeft ? BRIDGE_LEFT_X : BRIDGE_RIGHT_X;
  const midX = VIEW_WIDTH / 2;
  const description = shown
    ? `Galvanic cell. Anode ${shown.anode.label} on the ${anodeIsLeft ? 'left' : 'right'}, cathode ${shown.cathode.label}. Electrons flow through the wire from anode to cathode. In the salt bridge ${SALT_BRIDGE.cation} moves toward the cathode and ${SALT_BRIDGE.anion} toward the anode. E°cell ${formatSigned(shown.eCellV)} V.`
    : `Two half-cells joined by a wire and a salt bridge: ${first.label} on the left and ${second.label} on the right. Predict the cathode to reveal electron flow.`;

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={description}
    >
      <defs>
        <ArrowMarker id={`${markerId}-e`} className="fill-brand-coral" />
        <ArrowMarker id={`${markerId}-ion`} className="fill-ink" />
      </defs>
      <path
        d={`M${leftX} ${ELECTRODE_TOP} V${WIRE_Y} H${rightX} V${ELECTRODE_TOP}`}
        className="fill-none stroke-ink"
        strokeWidth="3"
      />
      <circle cx={midX} cy={WIRE_Y} r="24" className="fill-canvas-pure stroke-ink" strokeWidth="3" />
      <text x={midX} y={WIRE_Y + 6} textAnchor="middle" className="fill-ink text-[18px] font-extrabold">
        V
      </text>
      {shown ? (
        <g>
          <text x={midX} y={WIRE_Y + 44} textAnchor="middle" className="fill-ink text-[14px] font-extrabold">
            E°cell = {formatSigned(shown.eCellV)} V
          </text>
          {electronSegments.map(([from, to]) => (
            <line
              key={`${from}-${to}`}
              x1={from}
              y1={WIRE_Y - 14}
              x2={to}
              y2={WIRE_Y - 14}
              className="stroke-brand-coral"
              strokeWidth="3"
              markerEnd={`url(#${markerId}-e)`}
            />
          ))}
          <text x={midX} y={WIRE_Y - 32} textAnchor="middle" className="fill-ink text-[13px] font-extrabold">
            e⁻ flow: anode → cathode
          </text>
        </g>
      ) : null}

      <path
        d={`M${BRIDGE_LEFT_X} ${LIQUID_TOP + 40} V${BRIDGE_Y} H${BRIDGE_RIGHT_X} V${LIQUID_TOP + 40}`}
        className="fill-none stroke-brand-sunshine"
        strokeWidth="16"
        strokeLinejoin="round"
      />
      <text x={midX} y={BRIDGE_Y - 14} textAnchor="middle" className="fill-ink text-[13px] font-bold">
        Salt bridge · {SALT_BRIDGE.electrolyte}
      </text>
      {shown ? (
        <g>
          <line
            x1={midX + (anodeIsLeft ? 10 : -10)}
            y1={BRIDGE_Y + 30}
            x2={cathodeLegX + (anodeIsLeft ? -14 : 14)}
            y2={BRIDGE_Y + 30}
            className="stroke-ink"
            strokeWidth="2"
            markerEnd={`url(#${markerId}-ion)`}
          />
          <text
            x={(midX + cathodeLegX) / 2}
            y={BRIDGE_Y + 24}
            textAnchor="middle"
            className="fill-ink text-[12px] font-bold"
          >
            {SALT_BRIDGE.cation} → cathode
          </text>
          <line
            x1={midX + (anodeIsLeft ? -10 : 10)}
            y1={BRIDGE_Y + 52}
            x2={anodeLegX + (anodeIsLeft ? 14 : -14)}
            y2={BRIDGE_Y + 52}
            className="stroke-ink"
            strokeWidth="2"
            markerEnd={`url(#${markerId}-ion)`}
          />
          <text
            x={(midX + anodeLegX) / 2}
            y={BRIDGE_Y + 46}
            textAnchor="middle"
            className="fill-ink text-[12px] font-bold"
          >
            {SALT_BRIDGE.anion} → anode
          </text>
        </g>
      ) : null}

      <Vessel
        vessel={LEFT_VESSEL}
        liquidClass="fill-wash-sky"
        electrode={first.electrodeLabel}
        electrodeInert={first.electrodeKind === 'inert'}
        electrolyte={first.electrolyteLabel}
      />
      <Vessel
        vessel={RIGHT_VESSEL}
        liquidClass="fill-wash-mint"
        electrode={second.electrodeLabel}
        electrodeInert={second.electrodeKind === 'inert'}
        electrolyte={second.electrolyteLabel}
      />
      <text x={leftX} y={VESSEL_BOTTOM + 22} textAnchor="middle" className="fill-ink text-[14px] font-extrabold">
        {roleFor(first, `A: ${first.label}`)}
      </text>
      <text x={rightX} y={VESSEL_BOTTOM + 22} textAnchor="middle" className="fill-ink text-[14px] font-extrabold">
        {roleFor(second, `B: ${second.label}`)}
      </text>
      {shown ? (
        <>
          <text x={leftX} y={VESSEL_BOTTOM + 40} textAnchor="middle" className="fill-slate2 text-[12px] font-bold">
            {first.label}
          </text>
          <text x={rightX} y={VESSEL_BOTTOM + 40} textAnchor="middle" className="fill-slate2 text-[12px] font-bold">
            {second.label}
          </text>
        </>
      ) : null}
    </svg>
  );
}

const CELL_VESSEL = { x: 150, width: 340 };
const ANODE_X = 240;
const CATHODE_X = 400;

export function ElectrolysisSchematic({
  item,
  revealed,
  cathodeProduct,
  anodeProduct,
}: {
  item: ElectrolysisCase;
  revealed: boolean;
  cathodeProduct: string;
  anodeProduct: string;
}) {
  const markerId = `${useId().replace(/:/g, '')}-arrow`;
  const molten = item.kind === 'molten';
  const midX = VIEW_WIDTH / 2;
  const description = revealed
    ? `Electrolytic cell for ${item.name}. Anode (+) on the left forms ${anodeProduct}; cathode (−) on the right forms ${cathodeProduct}. Cations move to the cathode and anions move to the anode.`
    : `Electrolytic cell for ${item.name}: anode (+) on the left and cathode (−) on the right, connected to a DC power supply.`;
  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={description}
    >
      <defs>
        <ArrowMarker id={`${markerId}-e`} className="fill-brand-coral" />
        <ArrowMarker id={`${markerId}-ion`} className="fill-ink" />
      </defs>
      <rect
        x={CELL_VESSEL.x + 2}
        y={LIQUID_TOP}
        width={CELL_VESSEL.width - 4}
        height={VESSEL_BOTTOM - LIQUID_TOP - 2}
        className={molten ? 'fill-wash-coral' : 'fill-wash-sky'}
      />
      <path
        d={`M${CELL_VESSEL.x} ${VESSEL_TOP} V${VESSEL_BOTTOM} H${CELL_VESSEL.x + CELL_VESSEL.width} V${VESSEL_TOP}`}
        className="fill-none stroke-ink"
        strokeWidth="3"
      />
      <text x={midX} y={VESSEL_BOTTOM - 10} textAnchor="middle" className="fill-ink text-[13px] font-bold">
        {molten ? 'Molten electrolyte (no water)' : 'Aqueous solution (water present)'}
      </text>

      <path d={`M${ANODE_X} ${ELECTRODE_TOP} V${WIRE_Y} H${midX - 14}`} className="fill-none stroke-ink" strokeWidth="3" />
      <path d={`M${CATHODE_X} ${ELECTRODE_TOP} V${WIRE_Y} H${midX + 14}`} className="fill-none stroke-ink" strokeWidth="3" />
      <line x1={midX - 10} y1={WIRE_Y - 22} x2={midX - 10} y2={WIRE_Y + 22} className="stroke-ink" strokeWidth="4" />
      <line x1={midX + 10} y1={WIRE_Y - 12} x2={midX + 10} y2={WIRE_Y + 12} className="stroke-ink" strokeWidth="6" />
      <text x={midX - 26} y={WIRE_Y - 26} textAnchor="middle" className="fill-ink text-[16px] font-extrabold">
        +
      </text>
      <text x={midX + 26} y={WIRE_Y - 26} textAnchor="middle" className="fill-ink text-[16px] font-extrabold">
        −
      </text>
      <text x={midX} y={WIRE_Y + 42} textAnchor="middle" className="fill-slate2 text-[12px] font-bold">
        DC power supply
      </text>

      {[ANODE_X, CATHODE_X].map((x) => (
        <rect
          key={x}
          x={x - ELECTRODE_WIDTH / 2}
          y={ELECTRODE_TOP}
          width={ELECTRODE_WIDTH}
          height={ELECTRODE_HEIGHT}
          rx="3"
          className="fill-slate2 stroke-ink"
          strokeWidth="2"
        />
      ))}
      <text x={ANODE_X - 20} y={ELECTRODE_TOP + 14} textAnchor="end" className="fill-ink text-[14px] font-extrabold">
        Anode (+)
      </text>
      <text x={CATHODE_X + 20} y={ELECTRODE_TOP + 14} className="fill-ink text-[14px] font-extrabold">
        Cathode (−)
      </text>

      {revealed ? (
        <g>
          <line
            x1={ANODE_X - 22}
            y1={ELECTRODE_TOP - 4}
            x2={ANODE_X - 22}
            y2={WIRE_Y + 14}
            className="stroke-brand-coral"
            strokeWidth="3"
            markerEnd={`url(#${markerId}-e)`}
          />
          <line
            x1={CATHODE_X + 22}
            y1={WIRE_Y + 14}
            x2={CATHODE_X + 22}
            y2={ELECTRODE_TOP - 4}
            className="stroke-brand-coral"
            strokeWidth="3"
            markerEnd={`url(#${markerId}-e)`}
          />
          <text x={ANODE_X - 30} y={WIRE_Y + 40} textAnchor="end" className="fill-ink text-[12px] font-bold">
            e⁻
          </text>
          <text x={CATHODE_X + 30} y={WIRE_Y + 40} className="fill-ink text-[12px] font-bold">
            e⁻
          </text>
          <line
            x1={midX - 30}
            y1={LIQUID_TOP + 30}
            x2={CATHODE_X - 20}
            y2={LIQUID_TOP + 30}
            className="stroke-ink"
            strokeWidth="2"
            markerEnd={`url(#${markerId}-ion)`}
          />
          <text x={midX + 10} y={LIQUID_TOP + 22} textAnchor="middle" className="fill-ink text-[12px] font-bold">
            {item.cations.join(', ')} →
          </text>
          <line
            x1={midX + 30}
            y1={LIQUID_TOP + 64}
            x2={ANODE_X + 20}
            y2={LIQUID_TOP + 64}
            className="stroke-ink"
            strokeWidth="2"
            markerEnd={`url(#${markerId}-ion)`}
          />
          <text x={midX - 10} y={LIQUID_TOP + 56} textAnchor="middle" className="fill-ink text-[12px] font-bold">
            ← {item.anions.join(', ')}
          </text>
          <text x={ANODE_X} y={VESSEL_BOTTOM + 22} textAnchor="middle" className="fill-ink text-[13px] font-extrabold">
            {anodeProduct}
          </text>
          <text x={CATHODE_X} y={VESSEL_BOTTOM + 22} textAnchor="middle" className="fill-ink text-[13px] font-extrabold">
            {cathodeProduct}
          </text>
        </g>
      ) : null}
    </svg>
  );
}
